import { storage, getConfig } from "@byscott-io/corebyscott";

const API_BASE = "/api";

async function request(path, options = {}) {
  const token = storage.getToken();
  const containerId = storage.getContainerId();
  const config = getConfig();

  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  if (containerId) {
    headers[config.containerHeader] = containerId;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));
    throw new Error(error.error || "Request failed");
  }

  return response.json();
}

export const api = {
  // Dashboard
  getDashboardStats: () => request("/dashboard/stats"),

  // Events
  getEvents: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/dashboard/events?${query}`);
  },
  getEventTypes: () => request("/dashboard/event_types"),

  // Values
  getValues: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/dashboard/values?${query}`);
  },
  getMetricDefinitions: () => request("/dashboard/metric_definitions"),
  getValueHistory: (metric, entityType, entityId) => {
    const query = new URLSearchParams({ metric, entity_type: entityType, entity_id: entityId }).toString();
    return request(`/dashboard/value_history?${query}`);
  },

  // Referral funnel (#611)
  getReferralFunnel: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/dashboard/referral_funnel?${query}`);
  },

  // MetricsQL — run/validate a query string (same engine as the run_query MCP tool)
  runQuery: (appId, query) =>
    request("/dashboard/query", { method: "POST", body: JSON.stringify({ app_id: appId, query }) }),
  validateQuery: (query) =>
    request("/dashboard/query/validate", { method: "POST", body: JSON.stringify({ query }) }),
  drillQuery: (appId, query, cell, columns) =>
    request("/dashboard/query/detail", { method: "POST", body: JSON.stringify({ app_id: appId, query, cell, columns }) }),
  askQuery: (appId, question) =>
    request("/dashboard/query/ask", { method: "POST", body: JSON.stringify({ app_id: appId, question }) }),
  runSavedReport: (id) => request(`/dashboard/saved_reports/${id}/run`, { method: "POST" }),

  // Explore / insights — flexible multi-series query (POST body)
  runInsights: (body) => request("/dashboard/insights/query", { method: "POST", body: JSON.stringify(body) }),
  getInsightProperties: (appId, event) =>
    request(`/dashboard/insights/properties?${new URLSearchParams({ app_id: appId, ...(event ? { event } : {}) }).toString()}`),
  getInsightValues: (appId, event, property) =>
    request(`/dashboard/insights/values?${new URLSearchParams({ app_id: appId, ...(event ? { event } : {}), property }).toString()}`),

  // Saved reports (Reports dashboard tiles)
  getSavedReports: (params) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : "";
    return request(`/dashboard/saved_reports${query}`);
  },
  createSavedReport: (data) => request("/dashboard/saved_reports", { method: "POST", body: JSON.stringify(data) }),
  deleteSavedReport: (id) => request(`/dashboard/saved_reports/${id}`, { method: "DELETE" }),

  // Visitors (per-app visitor analytics)
  getVisitors: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/dashboard/visitors?${query}`);
  },
  getVisitor: (visitorId, params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/dashboard/visitors/${encodeURIComponent(visitorId)}?${query}`);
  },

  // Apps
  getApps: () => request("/dashboard/apps"),
  getApp: (id) => request(`/dashboard/apps/${id}`),
  createApp: (data) => request("/dashboard/apps", { method: "POST", body: JSON.stringify(data) }),
  updateApp: (id, data) => request(`/dashboard/apps/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteApp: (id) => request(`/dashboard/apps/${id}`, { method: "DELETE" }),
  regenerateApiKey: (appId) => request(`/dashboard/apps/${appId}/regenerate_key`, { method: "POST" }),
  getAppSettings: (appId) => request(`/dashboard/apps/${appId}/settings`),
  updateAppSettings: (appId, settings) => request(`/dashboard/apps/${appId}/update_settings`, {
    method: "PATCH",
    body: JSON.stringify({ settings }),
  }),
};
